import React, { useState, useRef } from 'react';
import { usePipeline, useBulkFeedback } from '../../hooks/useMarketing';
import PipelineColumn from './PipelineColumn';
import PipelineFilters from './PipelineFilters';
import PipelineSummary from './PipelineSummary';
import OutreachStageLegend from './OutreachStageLegend';

// Report-first workflow. Legacy `first_contact` records are bucketed into
// the Report sent column by the pipeline endpoint (stored values are never
// rewritten). `not_contacted` = quote-requesters awaiting their report —
// actioned via the Reports queue, so the column renders muted.
const STAGES = [
  { key: 'not_contacted', label: 'Awaiting report', color: '#f59e0b', muted: true },
  { key: 'report_sent', label: 'Report sent', color: '#0f766e' },
  { key: 'checkout_nudge', label: 'First nudge', color: '#d97706' },
  { key: 'second_contact', label: 'Second nudge', color: '#8b5cf6' },
  { key: 'call', label: 'Call list', color: '#0ea5e9' },
  { key: 'feedback', label: 'Feedback', color: '#64748b' },
  { key: 'responded', label: 'Responded', color: '#10b981' },
  { key: 'paid', label: 'Paid', color: '#22c55e' },
  { key: 'do_not_contact', label: 'Do Not Contact', color: '#ef4444' },
  { key: 'not_serious', label: 'Not Serious', color: '#94a3b8' },
];

function Pipeline() {
  const [filters, setFilters] = useState({});
  const [dueOnly, setDueOnly] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const { data, isLoading, error, refetch } = usePipeline(filters);
  const bulkFeedback = useBulkFeedback();
  const boardRef = useRef(null);

  const scrollBoard = (dir) => {
    boardRef.current?.scrollBy({ left: dir * 640, behavior: 'smooth' });
  };

  const toggleSelect = (visitorId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(visitorId)) next.delete(visitorId);
      else next.add(visitorId);
      return next;
    });
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  const handleBulkSend = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Send the feedback-ask email to ${ids.length} selected ${ids.length === 1 ? 'person' : 'people'}?\n\nAnyone already asked in the last 30 days, who already gave feedback, or who is DNC/paid will be skipped automatically.`)) {
      return;
    }
    try {
      const result = await bulkFeedback.mutateAsync(ids);
      const { summary, skipped } = result;
      const skippedDetail = (skipped || []).slice(0, 8).map((s) => `• ${s.email || s.visitorId}: ${s.reason}`).join('\n');
      window.alert(`Feedback ask sent to ${summary.sent} ${summary.sent === 1 ? 'person' : 'people'}.\nSkipped ${summary.skipped}, failed ${summary.failed}.${skippedDetail ? `\n\nSkipped:\n${skippedDetail}` : ''}`);
      exitSelectMode();
    } catch (err) {
      window.alert(`Bulk send failed: ${err.message}`);
    }
  };

  if (isLoading) {
    return (
      <div className="mkt-pipeline-loading">
        <div className="mkt-loading-spinner" />
        <p>Loading pipeline...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mkt-pipeline-error">
        <p>Failed to load pipeline data</p>
        <button onClick={() => refetch()} className="mkt-btn mkt-btn-secondary">
          Retry
        </button>
      </div>
    );
  }

  const { pipeline = {}, summary = {} } = data || {};
  const dueCount = summary.followUpsDue || 0;

  return (
    <div className="mkt-pipeline">
      <div className="mkt-pipeline-header">
        <PipelineFilters filters={filters} onChange={setFilters} />
        <button
          onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
          className={`mkt-btn ${selectMode ? 'mkt-btn-primary' : 'mkt-btn-secondary'}`}
          title="Select people to send a bulk feedback ask"
          style={{ whiteSpace: 'nowrap' }}
        >
          {selectMode ? '✕ Cancel selection' : '☑️ Bulk select'}
        </button>
        <button onClick={() => refetch()} className="mkt-btn mkt-btn-icon" title="Refresh">
          🔄
        </button>
      </div>

      {selectMode && (
        <div className="mkt-bulk-bar">
          <span>
            <strong>{selected.size}</strong> selected — tap cards to select. Tip: use the
            {' '}<em>Last Activity</em> filter to find people who went quiet a while ago.
          </span>
          <button
            className="mkt-btn mkt-btn-primary"
            disabled={selected.size === 0 || bulkFeedback.isPending}
            onClick={handleBulkSend}
          >
            {bulkFeedback.isPending ? 'Sending…' : `💬 Send feedback ask (${selected.size})`}
          </button>
        </div>
      )}

      {/* Today's queue — who needs a touch */}
      <div
        className="mkt-pipeline-due-banner"
        style={{
          display: 'flex', alignItems: 'center', gap: '12px', margin: '8px 0',
          padding: '10px 14px', borderRadius: '8px',
          background: dueCount > 0 ? '#fffbeb' : '#f8fafc',
          border: `1px solid ${dueCount > 0 ? '#fcd34d' : '#e2e8f0'}`,
        }}
      >
        <span style={{ fontSize: '14px', color: '#334155' }}>
          {dueCount > 0
            ? <><strong>🔔 {dueCount} {dueCount === 1 ? 'person needs' : 'people need'} a follow-up</strong> — they're sorted to the top of each column.</>
            : <>✅ No follow-ups due right now.</>}
        </span>
        {dueCount > 0 && (
          <button
            className="mkt-btn mkt-btn-secondary"
            style={{ marginLeft: 'auto', fontSize: '12px', padding: '4px 12px' }}
            onClick={() => setDueOnly(!dueOnly)}
          >
            {dueOnly ? 'Show everyone' : 'Show only due'}
          </button>
        )}
      </div>

      <PipelineSummary summary={summary} />
      <OutreachStageLegend />

      <div className="mkt-board-wrap">
        <button className="mkt-board-arrow left" onClick={() => scrollBoard(-1)} aria-label="Scroll board left">‹</button>
        <div className="mkt-pipeline-board" ref={boardRef}>
          {STAGES.map((stage) => (
            <PipelineColumn
              key={stage.key}
              stage={stage}
              visitors={(pipeline[stage.key] || []).filter(v => !dueOnly || v.followUpDue)}
              selectMode={selectMode}
              selected={selected}
              onToggleSelect={toggleSelect}
            />
          ))}
        </div>
        <button className="mkt-board-arrow right" onClick={() => scrollBoard(1)} aria-label="Scroll board right">›</button>
      </div>
    </div>
  );
}

export default Pipeline;
